import { Box, Button, Tooltip } from "@mui/material";
import { FormActionsProps } from "../types/PresentationFormTypes";
/**
 * ============================================================================
 * FORM ACTIONS - KOMPONENT PRZYCISKÓW AKCJI FORMULARZA
 * ============================================================================
 *
 * CEL KOMPONENTU:
 * - Wyświetla przyciski akcji formularza (Anuluj, Submit)
 * - Obsługuje stany przycisków (aktywny/wyłączony)
 * - Wyświetla tooltip z informacją o wymaganych polach
 * - Zapewnia spójny wygląd przycisków w całej aplikacji
 *
 * ARCHITEKTURA:
 * - Presentational Component: Tylko wyświetla przyciski, nie zarządza stanem
 * - Material-UI: Używa komponentów Button i Tooltip
 * - Accessibility: Obsługuje tooltip i stany disabled
 * - Styling: Spójne kolory i rozmiary przycisków
 *
 * PRZEPŁYW DANYCH:
 * 1. Otrzymuje props z funkcjami callback i stanem
 * 2. Generuje tooltip na podstawie stanu disabled
 * 3. Renderuje przyciski z odpowiednimi stylami
 * 4. Obsługuje kliknięcia przez callback functions
 *
 * PRZYCISKI:
 * - Anuluj: Zawsze aktywny, wywołuje onCancel
 * - Submit: Może być wyłączony, wywołuje onSubmit
 * - Tooltip: Pokazuje komunikat gdy submit jest wyłączony
 *
 * STYLING:
 * - Anuluj: Outlined button z niebieskim kolorem
 * - Submit: Contained button z zielonym kolorem
 * - Disabled: Szary kolor dla wyłączonych przycisków
 */

export default function FormActions({
  onSubmit,
  onCancel,
  disabled,
  submitText,
  registrationBlocked = false,
}: FormActionsProps) {
  // ============================================================================
  // GENEROWANIE KOMUNIKATU TOOLTIP
  // ============================================================================

  // Generuje komunikat tooltip na podstawie stanu przycisku submit
  // Wejście: disabled (boolean - czy przycisk jest wyłączony), registrationBlocked (boolean - czy rejestracja została zamknięta)
  // Wyjście: string (komunikat tooltip lub pusty string)
  const getTooltipMessage = () => {
    if (registrationBlocked) {
      return "Rejestracja została zakończona. Nie można dodać/edytować prezentacji.";
    }
    if (disabled) {
      return "Wypełnij wszystkie wymagane pola";
    }
    return "";
  };

  // Przycisk jest wyłączony jeśli disabled LUB registrationBlocked
  const isButtonDisabled = disabled || registrationBlocked;

  // ============================================================================
  // RENDEROWANIE PRZYCISKÓW AKCJI
  // ============================================================================

  return (
    <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
      {/* ========================================================================
          PRZYCISK ANULUJ
          ======================================================================== */}

      <Button
        variant="outlined"
        onClick={onCancel} // Handler anulowania formularza
        sx={{
          color: "#6366f1", // Niebieski kolor tekstu
          borderColor: "#6366f1", // Niebieska ramka
          "&:hover": {
            backgroundColor: "#f1e7de", // Jasne tło przy hover
            borderColor: "#6366f1", // Niebieska ramka przy hover
          },
          py: 1, // Padding pionowy
          px: 2, // Padding poziomy
          fontSize: "0.75rem", // Rozmiar czcionki
        }}
      >
        Anuluj
      </Button>

      {/* ========================================================================
          PRZYCISK SUBMIT Z TOOLTIP
          ======================================================================== */}

      <Tooltip title={getTooltipMessage()} arrow placement="top">
        <span>
          <Button
            variant="contained"
            onClick={onSubmit} // Handler submitowania formularza
            disabled={isButtonDisabled} // Stan wyłączony na podstawie walidacji lub zamknięcia rejestracji
            sx={{
              backgroundColor: "#10b981", // Zielony kolor tła
              "&:hover": {
                backgroundColor: "#059669", // Ciemniejszy zielony przy hover
              },
              "&:disabled": {
                backgroundColor: "#d1d5db", // Szary kolor dla wyłączonych
              },
              py: 1, // Padding pionowy
              px: 2, // Padding poziomy
              fontSize: "0.75rem", // Rozmiar czcionki
            }}
          >
            {submitText} {/* Dynamiczny tekst przycisku */}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}
